export const SRI_LANKA_TIME_ZONE = "Asia/Colombo";

const localDateTimePattern = /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})(?::(\d{2})(?:\.(\d+))?)?$/;
const hasOffset = (value) => /(?:Z|[+-]\d{2}:?\d{2})$/i.test(value);

const datePartsAtZone = (date, timeZone) => {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hourCycle: "h23",
  }).formatToParts(date);
  return Object.fromEntries(parts.filter((part) => part.type !== "literal").map(({ type, value }) => [type, value]));
};

const parseWallDateTime = (value) => {
  const match = localDateTimePattern.exec(value);
  if (!match) return null;
  const [, year, month, day, hour, minute, second = "0", fraction = "0"] = match;
  return {
    year: Number(year),
    month: Number(month),
    day: Number(day),
    hour: Number(hour),
    minute: Number(minute),
    second: Number(second),
    millisecond: Number(String(fraction).padEnd(3, "0").slice(0, 3)),
  };
};

const wallFields = (value) => {
  if (value instanceof Date) return datePartsAtZone(value, SRI_LANKA_TIME_ZONE);
  if (typeof value !== "string") return null;
  if (hasOffset(value)) {
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : datePartsAtZone(parsed, SRI_LANKA_TIME_ZONE);
  }
  const wall = parseWallDateTime(value);
  if (wall) return wall;
  const dateMatch = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  return dateMatch
    ? { year: Number(dateMatch[1]), month: Number(dateMatch[2]), day: Number(dateMatch[3]), hour: 0, minute: 0, second: 0 }
    : null;
};

export const sriLankaToday = (now = new Date()) => {
  const parts = datePartsAtZone(now instanceof Date ? now : new Date(now), SRI_LANKA_TIME_ZONE);
  return `${parts.year}-${parts.month}-${parts.day}`;
};

export const sriLankaLocalDateTimeInputValue = (now = new Date()) => {
  const parts = datePartsAtZone(now instanceof Date ? now : new Date(now), SRI_LANKA_TIME_ZONE);
  return `${parts.year}-${parts.month}-${parts.day}T${parts.hour}:${parts.minute}`;
};

export const sriLankaDateParts = (value) => {
  const parts = wallFields(value);
  if (!parts) return null;
  return {
    year: Number(parts.year), month: Number(parts.month), day: Number(parts.day),
    hour: Number(parts.hour || 0), minute: Number(parts.minute || 0),
    second: Number(parts.second || 0),
  };
};

export const businessDateTimeValue = (value) => {
  const parts = sriLankaDateParts(value);
  if (!parts) return Number.NaN;
  return Date.UTC(parts.year, parts.month - 1, parts.day, parts.hour, parts.minute, parts.second);
};

export const formatSriLankaDateTime = (value, options = {}) => {
  if (!value) return "-";
  const isWallString = typeof value === "string" && !hasOffset(value);
  const date = value instanceof Date
    ? value
    : isWallString && parseWallDateTime(value)
      ? new Date(businessDateTimeValue(value))
      : new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat("en-GB", {
    year: "numeric", month: "short", day: "2-digit",
    hour: "2-digit", minute: "2-digit", hourCycle: "h23",
    timeZone: isWallString ? "UTC" : SRI_LANKA_TIME_ZONE,
    ...options,
  }).format(date);
};

export const formatSriLankaDate = (value, options = {}) => {
  if (!value) return "-";
  const isDateOnly = typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value);
  const isWallString = typeof value === "string" && !hasOffset(value);
  const parts = sriLankaDateParts(value);
  const date = value instanceof Date || hasOffset(String(value))
    ? new Date(value)
    : parts
      ? new Date(Date.UTC(parts.year, parts.month - 1, parts.day, 12))
      : new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat("en-GB", {
    year: "numeric", month: "short", day: "2-digit",
    timeZone: isDateOnly || isWallString ? "UTC" : SRI_LANKA_TIME_ZONE,
    ...options,
  }).format(date);
};

export const sriLankaYear = (value) => sriLankaDateParts(value)?.year ?? null;
